import React, { useCallback, useEffect, useMemo } from "react";

import Grid from "@material-ui/core/Grid";
import { getIn, useField, useFormikContext } from "formik";
import { useSelector } from "react-redux";

import { SubtleBadge } from "components/Badge";
import {
  AlternateLabel,
  AlternativeProps,
} from "components/Business/BusinessForm/lib";
import { SlimButton, SlimSecondaryButton } from "components/Button";
import { Warning } from "components/ErrorMessage";
import { CollapseLabel } from "components/Form";
import { withNamespace, withNamespaces } from "components/Form/FormikControls";
import { EmailRecipientIconList } from "components/Icons/EmailRecipientIconList";
import { PrimaryContactIcon } from "components/Icons/PrimaryContactIcon";
import { CenteredGreedy, Row } from "components/Layout";
import Table, { CellRightAlign } from "components/Table";

import { AlternativeType } from "constants/businesses";
import { userTypes } from "constants/users";

import { pluralize } from "lib/pluralize";

import {
  getBusinessById,
  getDeploymentById,
  getSaleyards,
  selectIsUserOfType,
} from "selectors";

import AgriNousActiveLogo from "img/AgriNousActiveLogo";

import EmailRecipientForm from "./EmailRecipientForm";

const UserTypes = {
  EMAIL: "EMAIL",
  USER: "USER",
} as const;

type UserType = (typeof UserTypes)[keyof typeof UserTypes];

interface EmailRecipientWithSource extends EmailRecipient {
  source: {
    type: string;
    id: string;
  };
}

// This is somewhat a munge of EmailRecipient and BusinessBusinessUser
interface TableRowData {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  userType: UserType;
  saleYards?: string[];
  source?: {
    type: string;
    id: string;
  };
  managedBy?: {
    type: string;
    id: string;
  };
  [key: string]: any;
}

interface PrimaryContactWarningProps {
  email: string | null;
  fallbackEmail: string | null;
}

const PrimaryContactWarning: React.FC<PrimaryContactWarningProps> = ({
  email,
  fallbackEmail,
}) => {
  const fallbackEmailWarning = fallbackEmail
    ? `${fallbackEmail} will be used as the primary contact for now.`
    : "";
  const emailWarning = email
    ? `${email} is not a known email recipient.`
    : `No primary contact set. ${fallbackEmailWarning}`;
  return (
    <CenteredGreedy>
      <Warning>
        {emailWarning} Please set one of the email recipients below as the
        primary contact!
      </Warning>
    </CenteredGreedy>
  );
};

interface TableRow {
  value: any;
  original: TableRowData;
  row: TableRowData & { managedBy?: { type: string; id: string } };
}

const SaleYardsRenderer = (row: TableRow): string =>
  Array.isArray(row.value) && row.value.length > 0 ? row.value.join(", ") : "-";

interface ManagedByRendererProps {
  value?: { type: string; id: string } | null;
  row: TableRowData;
}

const ManagedByRenderer: React.FC<ManagedByRendererProps> = props => {
  const sourceName = useSelector(state => {
    if (props.value && props.row.userType === UserTypes.EMAIL) {
      if (props.value.type === AlternativeType.Deployment) {
        return getDeploymentById(props.value.id)(state)?.name || "";
      }
      if (props.value.type === AlternativeType.Saleyard) {
        return getSaleyards(state)[props.value.id]?.name || "";
      }
    }
    return null;
  });
  if (props.row.userType === UserTypes.USER) {
    return "AgriNous";
  }
  return sourceName;
};

const UserTypeCellRenderer = (row: TableRow): React.JSX.Element => (
  <CenteredGreedy>
    <AgriNousActiveLogo isActive={row.value === UserTypes.USER} />
  </CenteredGreedy>
);

interface ActionsCellRendererProps {
  original: TableRowData;
  row: TableRowData & { managedBy?: { type: string; id: string } };
}

const ActionsCellRendererFactory =
  (
    onSetPrimaryContactEmail: (email: string) => void,
    onEditEmailRecipientIndex: (id: number) => void,
    onRemoveEmailRecipientIndex: (id: number) => void,
    isUserSaleyardAdmin: boolean,
    isUserLivestockAgent: boolean,
    primaryContactEmail: string | null,
  ) =>
  ({ original, row }: ActionsCellRendererProps): React.JSX.Element => {
    const { email, id: emailRecipientId, userType } = original;
    const { managedBy } = row;

    const displayEmailRecipientButtons =
      userType === UserTypes.EMAIL &&
      ((managedBy?.type === AlternativeType.Deployment &&
        isUserLivestockAgent) ||
        (managedBy?.type === AlternativeType.Saleyard && isUserSaleyardAdmin));

    return (
      <CellRightAlign>
        <SlimButton
          disabled={email === primaryContactEmail}
          type="button"
          onClick={() => onSetPrimaryContactEmail(email)}
        >
          Set Primary
        </SlimButton>{" "}
        {displayEmailRecipientButtons ? (
          <SlimButton
            type="button"
            onClick={() => onEditEmailRecipientIndex(emailRecipientId)}
          >
            Edit
          </SlimButton>
        ) : null}{" "}
        {displayEmailRecipientButtons ? (
          <SlimButton
            backgroundColor="deleteRed"
            type="button"
            data-tour="delete"
            onClick={() => onRemoveEmailRecipientIndex(emailRecipientId)}
          >
            Delete
          </SlimButton>
        ) : null}
      </CellRightAlign>
    );
  };

interface PrimaryContactRendererProps {
  original: TableRowData;
}

const PrimaryContactRenderer =
  (
    fallbackContactEmail: string | null,
    hasPrimaryContact: boolean,
    primaryContactEmail: string | null,
  ) =>
  ({ original }: PrimaryContactRendererProps): React.JSX.Element | null => {
    const { email } = original;
    const isPrimaryContact = email === primaryContactEmail;
    const isFallbackContact = email === fallbackContactEmail;
    if (isPrimaryContact || (isFallbackContact && !hasPrimaryContact)) {
      return (
        <CenteredGreedy>
          <PrimaryContactIcon
            color={isPrimaryContact ? "primary" : "pending"}
            title={
              isPrimaryContact ? "Primary Contact" : "Temporary Primary Contact"
            }
          />
        </CenteredGreedy>
      );
    }
    return null;
  };

const AlternateLabelCellRendererFactory =
  (
    altFieldName: string,
    overrideFormikField: string,
    isEditingExistingEmailRecipient: boolean,
    values: Record<string, unknown>,
  ) =>
  (cellProps: TableRow): React.JSX.Element => {
    const alternativeProps: AlternativeProps = {
      original: cellProps.original as {
        alternatives?: Array<{
          source: { type: string; id: string };
          [key: string]: unknown;
        }>;
      },
      value: cellProps.value,
    };
    return (
      <AlternateLabel
        props={alternativeProps}
        altFieldName={altFieldName}
        overrideFormikField={overrideFormikField}
        disabledButton={!isEditingExistingEmailRecipient}
        values={values}
      />
    );
  };

const EmailRecipientIconListRendererFactory = (
  row: TableRow,
): React.JSX.Element => <EmailRecipientIconList recipient={row} />;

const DEFAULT_SORT = [
  {
    id: "firstName",
    direction: "asc" as const,
  },
];

interface EmailRecipientsHeaderProps {
  masterBusinessId: string;
  namespace: string;
  primaryContactEmail: string | null;
}

export function EmailRecipientsHeader(
  props: EmailRecipientsHeaderProps,
): React.JSX.Element {
  const { masterBusinessId, namespace: ns, primaryContactEmail } = props;
  const [{ value }] = useField<EmailRecipientWithSource[]>(ns);

  const numEmailRecipients = value.length;
  const numBusinessUsers = useSelector(
    (state: State) =>
      getBusinessById(masterBusinessId)(state)?.businessUsers.length || 0,
  );

  return (
    <Row>
      <SubtleBadge
        hasErrorBadge={numEmailRecipients === 0 || !primaryContactEmail}
      >
        Email Recipients
      </SubtleBadge>
      <Row alignCenter flexWrap>
        <CollapseLabel>
          {numEmailRecipients} Email{" "}
          {pluralize("Recipient", numEmailRecipients)}
        </CollapseLabel>
        <CollapseLabel>
          {numBusinessUsers} AgriNous {pluralize("User", numBusinessUsers)}
        </CollapseLabel>
        <CollapseLabel>
          Primary Contact: {primaryContactEmail || "Not Set"}
        </CollapseLabel>
      </Row>
    </Row>
  );
}

interface EmailRecipientsTableProps {
  masterBusinessId: string;
  primaryContactEmail: string | null;
  namespace: string;
  onEndEditing: () => void;
  onStartEditing: () => void;
  onSetPrimaryContactEmail: (email: string) => void;
}

export function EmailRecipientsTable(
  props: EmailRecipientsTableProps,
): React.JSX.Element {
  const {
    masterBusinessId,
    primaryContactEmail,
    namespace: ns,
    onEndEditing,
    onStartEditing,
    onSetPrimaryContactEmail,
  } = props;
  const formikContext = useFormikContext();
  const { dirty, isValid, values, setFieldValue, setTouched } = formikContext;
  const businessUsers = useSelector(
    (state: State) => getBusinessById(masterBusinessId)(state)?.businessUsers,
  );

  const isUserSaleyardAdmin = useSelector((state: State) =>
    selectIsUserOfType([userTypes.SALEYARD_ADMIN])(state),
  );
  const isUserLivestockAgent = useSelector((state: State) =>
    selectIsUserOfType([userTypes.LIVESTOCK_AGENT])(state),
  );

  const emailRecipients = getIn(values, withNamespace(ns, "values")) as
    | EmailRecipientWithSource[]
    | undefined;

  // to find equivalent of old server side fallback to self.email_recipients.first()
  // filter out my own type of EmailRecipients as serializer jams both types together
  const ownEmailRecipients = (emailRecipients || []).filter(
    (recipient: EmailRecipientWithSource) =>
      (isUserLivestockAgent &&
        recipient.source.type === AlternativeType.Deployment) ||
      (isUserSaleyardAdmin &&
        recipient.source.type === AlternativeType.Saleyard),
  );
  const firstEmail =
    ownEmailRecipients.length > 0 ? ownEmailRecipients[0].email : null;

  // Adapt the data to be show in the table, including both:
  //  - the SpecificBusiness EmailRecipients, and
  //  - the Master Businesses Registered Business Users (which are retrieved straight from the state for the most up-to-date data)
  const tableData = useMemo(
    (): TableRowData[] => [
      // Add the Email Recipients to the table data, with an additional `userType` attribute set to `UserTypes.EMAIL`
      ...(emailRecipients || []).map(
        (emailRecipient: EmailRecipientWithSource) => ({
          ...emailRecipient,
          userType: UserTypes.EMAIL as UserType,
        }),
      ),
      // Add the Registered Business Users to the table data, with an additional `userType` attribute set to `UserTypes.USER`
      ...(businessUsers || []).map((businessUser: BusinessBusinessUser) => ({
        ...businessUser,
        userType: UserTypes.USER as UserType,
      })),
    ],
    [businessUsers, emailRecipients],
  );

  const emailRecipientsCount = tableData.length;
  const hasEmailRecipients = Boolean(emailRecipientsCount);
  // any emailRecipient OR businessUser can be used as the primary Contact
  const hasPrimaryContact =
    primaryContactEmail &&
    tableData.some(recipient => recipient.email === primaryContactEmail);

  // Get the current Specific Business's EmailRecipients from the Formik state
  const editingEmailRecipient = getIn(
    values,
    withNamespace(ns, "editingValue"),
  ) as EmailRecipientWithSource | null | undefined;

  // Determine if the form is editing an EmailRecipient which already exists in the Formik state.
  // Note: this may not be the Redux state. This could be dirty data from the root Formik state, see DeploymentBusinessForm,
  // or the direct parent Formik state, see DeploymentRecipientTable
  const isEditingExistingEmailRecipient =
    editingEmailRecipient &&
    Array.isArray(emailRecipients) &&
    emailRecipients.some(
      (emailRecipient: EmailRecipientWithSource) =>
        emailRecipient.id === editingEmailRecipient.id,
    );

  const onEditEmailRecipientIndex = useCallback(
    (emailRecipientId: number) => {
      // Move a copy of the selected EmailRecipient to the `editingValues` field to fill in the form
      const recipient = emailRecipients?.find(
        ({ id }: EmailRecipientWithSource) => id === emailRecipientId,
      );
      if (recipient) {
        setFieldValue(withNamespace(ns, "editingValue"), {
          ...recipient,
        });
      }

      // Clear any Formik validation from being displayed
      setTouched({});
      onStartEditing();

      // This could be a long table, so scroll back to the _email_ `input`
      const inputName = withNamespaces([ns, "editingValue"], "email");
      const inputEl = document.querySelector(`input[name="${inputName}"]`);
      if (inputEl) {
        inputEl.scrollIntoView({ behavior: "smooth" });
      }
    },
    [emailRecipients, ns, onStartEditing, setFieldValue, setTouched],
  );

  const onRemoveEmailRecipientIndex = useCallback(
    (emailRecipientId: number) => {
      if (!emailRecipients) return;
      const newState = emailRecipients.slice();
      const index = newState.findIndex(
        ({ id }: EmailRecipientWithSource) => id === emailRecipientId,
      );
      if (index !== -1) {
        newState.splice(index, 1);
        setFieldValue(withNamespace(ns, "values"), newState, false);
        onStartEditing();
      }
    },
    [emailRecipients, ns, onStartEditing, setFieldValue],
  );

  function onClickCommitEmailRecipientChanges(): boolean {
    if (!emailRecipients || !editingEmailRecipient) return false;
    const newState = emailRecipients.slice();
    const existingIndex = newState.findIndex(
      (deploymentEmailRecipient: EmailRecipientWithSource) =>
        deploymentEmailRecipient.id === editingEmailRecipient.id,
    );
    if (existingIndex === -1) {
      // When this is a new DeploymentEmailRecipient
      newState.splice(newState.length, 1, {
        ...editingEmailRecipient,
        source: {
          type: isUserLivestockAgent
            ? AlternativeType.Deployment
            : AlternativeType.Saleyard,
          id: editingEmailRecipient.source?.id || "",
        },
        // Add a temporary (offline) id to allow the front end to track the changes to this entry.
        // Once submitted, and a response is returned, this will have and actual database primary key in here.
        id: -Date.now(),
      });

      if (emailRecipientsCount === 0 && editingEmailRecipient.email) {
        // adding the first contact - make it primary
        onSetPrimaryContactEmail(editingEmailRecipient.email);
      }
    } else {
      // When the user is editing an existing EmailRecipient
      newState.splice(existingIndex, 1, editingEmailRecipient);

      if (emailRecipientsCount === 1 && editingEmailRecipient.email) {
        // edited the only contact - make it primary
        onSetPrimaryContactEmail(editingEmailRecipient.email);
      }
    }

    setFieldValue(withNamespace(ns, "values"), newState, false);
    setFieldValue(withNamespace(ns, "editingValue"), null, true);

    setTouched({});
    return false;
  }

  function onClickClearEmailRecipientChanges(): void {
    setFieldValue(withNamespace(ns, "editingValue"), null, true);

    setTouched({});
  }

  useEffect(() => {
    // When something in the Formik context, or any of the props have changed, and it results in the form swapping from clean to dirty, or dirty to clean,
    // including all of the EmailRecipients and the new/edit form values, let the parent that is being edited
    if (dirty) {
      onStartEditing();
    } else {
      onEndEditing();
    }
  }, [dirty, onEndEditing, onStartEditing]);

  const columns = useMemo(
    () => [
      {
        id: "userType",
        Header: "",
        accessor: "userType",
        Cell: UserTypeCellRenderer,
        width: 80,
      },
      {
        Cell: PrimaryContactRenderer(
          firstEmail,
          hasPrimaryContact,
          primaryContactEmail,
        ),
        id: "primaryContact",
        Header: "Primary",
        width: 80,
      },
      {
        id: "firstName",
        Header: "First Name",
        accessor: "firstName",
        Cell: AlternateLabelCellRendererFactory(
          "first_name",
          "editingValue.firstName",
          isEditingExistingEmailRecipient,
          values as Record<string, unknown>,
        ),
      },
      {
        id: "lastName",
        Header: "Last Name",
        accessor: "lastName",
        Cell: AlternateLabelCellRendererFactory(
          "last_name",
          "editingValue.lastName",
          isEditingExistingEmailRecipient,
          values as Record<string, unknown>,
        ),
      },
      {
        id: "email",
        Header: "Email",
        accessor: "email",
        Cell: AlternateLabelCellRendererFactory(
          "email",
          "editingValue.email",
          isEditingExistingEmailRecipient,
          values as Record<string, unknown>,
        ),
      },
      {
        id: "phoneNumber",
        Header: "Phone Number",
        accessor: "phoneNumber",
        Cell: AlternateLabelCellRendererFactory(
          "phone_number",
          "editingValue.phoneNumber",
          isEditingExistingEmailRecipient,
          values as Record<string, unknown>,
        ),
      },
      {
        id: "recipientFor",
        Header: "Recipient For",
        accessor: EmailRecipientIconListRendererFactory,
      },
      {
        id: "saleYards",
        Header: "Sale Yards",
        accessor: "saleYards",
        Cell: SaleYardsRenderer,
        style: { whiteSpace: "unset" },
      },
      {
        accessor: "source",
        Cell: ManagedByRenderer,
        id: "managedBy",
        Header: "Managed By",
      },
      {
        Cell: ActionsCellRendererFactory(
          onSetPrimaryContactEmail,
          onEditEmailRecipientIndex,
          onRemoveEmailRecipientIndex,
          isUserSaleyardAdmin,
          isUserLivestockAgent,
          primaryContactEmail,
        ),
        Header: "Actions",
        id: "actions",
        width: 250,
      },
    ],
    [
      firstEmail,
      hasPrimaryContact,
      isEditingExistingEmailRecipient,
      isUserLivestockAgent,
      isUserSaleyardAdmin,
      onSetPrimaryContactEmail,
      onEditEmailRecipientIndex,
      onRemoveEmailRecipientIndex,
      primaryContactEmail,
      values,
    ],
  );

  const isClearEnabled = editingEmailRecipient !== null;
  const isAddUpdateEnabled = editingEmailRecipient !== null && dirty && isValid;
  const isRevertEnabled = dirty;
  const isSubmitEnabled = dirty && isValid && editingEmailRecipient === null;

  return (
    <>
      <Grid container spacing={2} alignItems="flex-end">
        <EmailRecipientForm namespace={withNamespace(ns, "editingValue")} />
        <Grid item xs={7} md={4}>
          <SlimSecondaryButton
            disabled={!isClearEnabled}
            onClick={onClickClearEmailRecipientChanges}
            type="button"
          >
            Clear
          </SlimSecondaryButton>
          <SlimButton
            disabled={!isAddUpdateEnabled}
            onClick={onClickCommitEmailRecipientChanges}
            type="button"
            data-tour="emailAdd"
          >
            {isEditingExistingEmailRecipient ? "Update" : "Add"} Email Recipient
          </SlimButton>
        </Grid>
      </Grid>
      {hasEmailRecipients && !hasPrimaryContact ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <PrimaryContactWarning
              email={primaryContactEmail}
              fallbackEmail={firstEmail}
            />
          </Grid>
        </Grid>
      ) : null}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Table
            columns={columns}
            data={tableData}
            defaultSorted={DEFAULT_SORT}
            noResultsMessage="This Business does not have any Email Recipients yet."
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid container item xs={12}>
          <SlimSecondaryButton type="reset" disabled={!isRevertEnabled}>
            Revert Changes
          </SlimSecondaryButton>
          <SlimButton
            type="submit"
            disabled={!isSubmitEnabled}
            data-tour="emailSubmit"
          >
            Accept Changes
          </SlimButton>
        </Grid>
      </Grid>
    </>
  );
}
